import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Building2, Landmark, LineChart, MapPin, Layers } from "lucide-react";
import { SITE } from "../lib/site";
import Testimonials from "../components/Testimonials";
import FAQ from "../components/FAQ";
import LandownerPack from "../components/LandownerPack";

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#0B0F0E]">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-24 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 stagger">
            <div className="overline">
              <span className="h-line" />A NEW-AGE STRATEGIC LAND COMPANY
            </div>
            <h1 className="font-display text-[#F4EFE6] text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] mt-8">
              Unlocking the<br/>
              <span className="italic-gold">true potential</span><br/>
              of British land.
            </h1>
            <p className="font-body text-[#9BA39B] text-lg md:text-xl max-w-2xl mt-8 leading-relaxed">
              Upsurge Strategic Land Ltd promotes greenfield, brownfield and urban-edge sites through the UK planning system —
              meeting the country&apos;s commercial and residential shortfall, at our cost and our risk.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-cta-sell-land" to="/sell-your-land" className="usl-btn">Get a Confidential Appraisal <ArrowUpRight size={16} /></Link>
              <Link data-testid="hero-cta-appraisal" to="/appraisal" className="usl-btn-outline">What&apos;s my land worth?</Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <img src={SITE.images.georgianTerrace} alt="Strategic land" className="w-full h-[520px] md:h-[620px] object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/70 to-transparent">
                <div className="overline">Featured Site</div>
                <div className="font-display text-2xl md:text-3xl text-[#F4EFE6] mt-2 leading-tight">
                  Cotswold Edge · <span className="italic-gold">Consent Granted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-14 grid md:grid-cols-4 gap-10">
          {SITE.stats.map((s, i) => (
            <div key={s.label} data-testid={`stat-${i}`}>
              <div className="font-display text-5xl md:text-6xl text-[#C9A24B]">{s.value}</div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#9BA39B] mt-3 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-28 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="overline"><span className="h-line" />What we do</div>
          <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
            A new-age approach to <span className="italic-gold">land uplift.</span>
          </h2>
          <p className="text-[#9BA39B] mt-7 max-w-md leading-relaxed">
            We blend modern planning expertise, viability craft and patient capital — taking on every cost and every risk so landowners benefit from consented value.
          </p>
          <Link to="/services" data-testid="home-services-link" className="usl-btn-outline mt-9">Explore Services <ArrowUpRight size={16} /></Link>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {[
            { icon: Leaf, title: "Greenfield Promotion", text: "Strategic farmland and urban-edge sites through Local Plan allocation." },
            { icon: Building2, title: "Brownfield Regeneration", text: "Remediation and consent on previously developed land — our core specialism." },
            { icon: Landmark, title: "Council Engagement", text: "Direct work with LPAs, parish councils and policy officers." },
            { icon: LineChart, title: "Viability & Uplift", text: "Robust value modelling — CIL, S106 and tilted-balance arguments." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} data-testid={`feature-${i}`} className="card-lift border border-[#F4EFE6]/10 p-8 bg-[#11171A]/60">
                <Icon className="text-[#C9A24B]" size={26} />
                <div className="font-display text-2xl text-[#F4EFE6] mt-6">{f.title}</div>
                <p className="text-[#9BA39B] text-sm mt-3 leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* BROWNFIELD FEATURE */}
      <section className="bg-[#11171A] border-y border-[#F4EFE6]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-28 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline"><span className="h-line" />Our specialism</div>
            <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
              Brownfield is <span className="italic-gold">where we excel.</span>
            </h2>
            <p className="text-[#9BA39B] mt-7 leading-relaxed max-w-xl">
              Britain&apos;s brownfield stock is the most underused source of housing and commercial floorspace in the country. Constraint is opportunity — we turn complex, contaminated and forgotten sites into deliverable, consented, viable schemes.
            </p>
            <ul className="mt-8 space-y-4 text-[15px] text-[#E8E1D2]">
              {[
                "Phase I / II site investigation strategy",
                "Remediation scoping & abnormal cost modelling",
                "Vacancy Building Credit & brownfield register submissions",
                "Mixed-use viability — commercial, BTR, PRS, traditional housing",
                "Heritage, contamination & townscape consenting",
              ].map((t) => (
                <li key={t} className="flex gap-3" data-testid={`brownfield-bullet-${t.slice(0, 20)}`}>
                  <span className="text-[#C9A24B] mt-1">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/sell-your-land" data-testid="brownfield-cta" className="usl-btn mt-10">Submit a Brownfield Site <ArrowUpRight size={16} /></Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 grid grid-cols-2 gap-4">
            <img src={SITE.images.brownfield} alt="Brownfield site" className="w-full h-[260px] md:h-[320px] object-cover col-span-1" />
            <img src={SITE.images.cityscape} alt="Urban regeneration" className="w-full h-[260px] md:h-[320px] object-cover col-span-1 mt-10" />
            <img src={SITE.images.brownfield2} alt="Industrial heritage" className="w-full h-[200px] md:h-[260px] object-cover col-span-2" />
          </div>
        </div>
      </section>

      {/* LOCAL AUTHORITY EXPERTISE */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-28">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <div className="overline"><span className="h-line" />Council &amp; Planning Policy</div>
            <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
              We work where <span className="italic-gold">policy meets land.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#9BA39B] leading-relaxed mt-3">
              Our team operates at the policy coalface — making the technical, political and viability case for your land within every relevant local authority process.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F4EFE6]/10 border border-[#F4EFE6]/10">
          {SITE.expertise.map((e, i) => (
            <div key={e.t} data-testid={`expertise-${i}`} className="bg-[#0B0F0E] p-7 card-lift">
              <div className="font-display text-[#C9A24B] text-2xl">0{i+1}</div>
              <div className="font-display text-xl text-[#F4EFE6] mt-3">{e.t}</div>
              <p className="text-[#9BA39B] text-sm mt-3 leading-relaxed">{e.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MARKET POSITIONING */}
      <section className="bg-[#11171A] border-y border-[#F4EFE6]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline"><span className="h-line" />Our market</div>
            <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
              The UK&apos;s <span className="italic-gold">shortfall</span> is our market.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-7">
            <p className="text-[#E8E1D2] text-lg leading-relaxed">
              Every local authority in England is held to a Five-Year Housing Land Supply and a Housing Delivery Test. Most are falling short. Commercial space — especially logistics, life sciences and BTR — faces a parallel under-supply.
            </p>
            <p className="text-[#9BA39B] leading-relaxed">
              Wherever there is a shortfall — residential or commercial, urban or rural — there is land to be promoted. Upsurge identifies, structures and delivers that uplift.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 pt-4">
              {[
                { v: "5-YR HLS", t: "Shortfall identification" },
                { v: "HDT", t: "Tilted-balance strategy" },
                { v: "GREEN BELT", t: "Exceptional-circumstance case" },
              ].map((b, i) => (
                <div key={b.v} data-testid={`market-${i}`} className="border-t border-[#C9A24B]/40 pt-4">
                  <div className="font-display text-[#C9A24B] text-xl tracking-wide">{b.v}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#9BA39B] mt-2 font-semibold">{b.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* LANDOWNER WATCH CTA */}
      <section data-testid="watch-cta-section" className="bg-[#0B0F0E] border-y border-[#F4EFE6]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline"><span className="h-line" />Landowner Watch · Free</div>
            <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
              We&apos;ll watch the planning system <span className="italic-gold">for you.</span>
            </h2>
            <p className="text-[#9BA39B] mt-6 max-w-2xl leading-relaxed">
              Free quarterly alerts on Local Plan activity, Five-Year Housing Land Supply changes and Green Belt reviews in your postcode area. Useful even if you never intend to sell.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/watch" data-testid="home-watch-cta" className="usl-btn">Subscribe — Free <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* LANDOWNER PACK */}
      <LandownerPack />

      {/* CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline"><span className="h-line" />Begin a conversation</div>
            <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
              Ready to realise your land&apos;s <span className="italic-gold">true value?</span>
            </h2>
            <p className="text-[#9BA39B] mt-6 max-w-2xl">Tell us about your site — greenfield, brownfield, residential or commercial. We provide a no-obligation appraisal, confidentially and at no cost.</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-4">
            <Link to="/sell-your-land" data-testid="cta-sell-land" className="usl-btn">Submit Your Land <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
